import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface ChartSeries {
  name: string;
  data: (number | null)[];
}

interface Props {
  data: { categories: string[]; series: ChartSeries[] };
  title?: string;
  neighborhoodName?: string;
  propertyTypeLabel?: string;
}

const FONT = "'Montserrat Light', 'Montserrat', sans-serif";
const FONT_SIZE = '11pt';
const FONT_STYLE = { fontFamily: FONT, fontSize: FONT_SIZE, fontWeight: 'normal' as const };

const COLORS = ['#738faf', '#b6b8ac', '#cbe7d4', '#e9d79a', '#909371', '#a7e4e6', '#d99a7b'];

export const SalesPriceDistributionChart = React.forwardRef<HighchartsReact.RefObject, Props>(
  ({ data, title = 'Sales Price Distribution', neighborhoodName = '', propertyTypeLabel = '' }, ref) => {
    const subtitle = `${neighborhoodName} | ${propertyTypeLabel} Sales by Price Range`;
    const options: Highcharts.Options = {
      chart: {
        type: 'column',
        height: 560,
        marginTop: 160,
        style: { fontFamily: FONT, fontSize: FONT_SIZE }
      },
      title: {
        text: title,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '30pt', fontWeight: '500', color: '#000000' }
      },
      subtitle: {
        text: subtitle,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '14pt', fontWeight: '500', color: '#66635F' }
      },
      xAxis: {
        categories: data.categories,
        title: { text: 'Year', style: FONT_STYLE },
        labels: { style: FONT_STYLE }
      },
      yAxis: {
        min: 0,
        max: 100,
        reversedStacks: false,
        title: { text: undefined },
        labels: { enabled: false }
      },
      legend: {
        itemStyle: FONT_STYLE
      },
      tooltip: {
        style: FONT_STYLE,
        pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.percentage:.1f}%</b><br/>',
        shared: false
      },
      plotOptions: {
        column: {
          stacking: 'percent',
          pointPadding: 0,
          groupPadding: 0.15,
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            format: '{point.percentage:.1f}%',
            style: { ...FONT_STYLE, textOutline: 'none', color: '#555' }
          }
        }
      },
      colors: COLORS,
      series: data.series.map(s => ({
        type: 'column' as const,
        name: s.name,
        data: s.data
      })),
      credits: { enabled: false }
    };

    return <HighchartsReact ref={ref} highcharts={Highcharts} options={options} />;
  }
);
